import type { DuplicateMatch } from "@/utils/models";
import { isStringArray } from "@/utils/typeGuards";
import { conflictDetailOf, isRecord } from "../conflictDetail";

/** i18n key every location write raises with a 409 when it would add a duplicate row (APP-780). */
export const DUPLICATE_LOCATION_KEY = "schedule.template.duplicate.location";

const isDuplicateMatch = (value: unknown): value is DuplicateMatch =>
	isRecord(value) && typeof value.candidateId === "string" && isStringArray(value.duplicateOfIds) && value.duplicateOfIds.length > 0;

/** The backend only raises when something matched, so an empty list is as broken as a malformed one. */
const isDuplicateMatchList = (value: unknown): value is DuplicateMatch[] => Array.isArray(value) && value.length > 0 && value.every(isDuplicateMatch);

/**
 * The rows a refused location save would have duplicated, read from the 409
 * `schedule.template.duplicate.location` body (`detail.details.duplicates`), one entry per refused
 * candidate. Reads a wrapping `Error`'s `cause` when the error itself isn't the response.
 *
 * Returns null for anything that isn't that 409, so every other failure keeps its snackbar path, and
 * for that 409 WITHOUT `details`: the unique-constraint fallback (an `IntegrityError` the rule check
 * didn't foresee) raises the same key with no matches to name. A `details.duplicates` that is present
 * but not the documented shape is a broken contract with the backend, so it throws instead of being
 * shown as a generic refusal.
 */
export function duplicateOfIdsFromError(error: unknown): DuplicateMatch[] | null {
	const detail = conflictDetailOf(error);
	if (detail === null || detail.message !== DUPLICATE_LOCATION_KEY || detail.details === undefined) return null;

	const { details } = detail;
	const duplicates = isRecord(details) ? details.duplicates : undefined;
	if (!isDuplicateMatchList(duplicates)) {
		throw new Error(`duplicateOfIdsFromError: a 409 ${DUPLICATE_LOCATION_KEY} carried malformed details.duplicates`, { cause: error });
	}
	return duplicates;
}
